import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://popprotents.com';

const applyJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/apply/#webpage`,
  name: 'Join the Team — Careers at Pop Pro Tents | Austin TX',
  description: 'Apply to join the Pop Pro Tents crew. Hands-on stretch tent installation work with flexible schedules, great team culture, and varied locations across Texas.',
  url: `${siteUrl}/apply`,
  isPartOf: {
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    name: 'Pop Pro Tents',
    url: siteUrl,
  },
  about: {
    '@type': 'Organization',
    '@id': `${siteUrl}/#business`,
    name: 'Pop Pro Tents',
  },
};

export default function ApplyPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    experience: '',
    availability: '',
    motivation: '',
    heardFrom: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.fullName || !formData.email || !formData.phone || !formData.motivation) {
      return;
    }

    if (formData.motivation.length > 500) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => body.append(key, value));

      const response = await fetch('https://readdy.ai/api/form/d6tvabpqcrg2vc5apuh0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          location: '',
          experience: '',
          availability: '',
          motivation: '',
          heardFrom: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(applyJsonLd) }}
      />
      <Navbar />

      {/* Hero Banner - extends to top edge, navbar floats over it */}
      <div className="relative overflow-hidden bg-teal-500">
        <div className="absolute inset-0">
          <img
            src="/assets/img/b747a48db7f3.webp"
            alt="Team at work"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-teal-600/70"></div>
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-44 pb-20">
          <h1 className="text-6xl md:text-7xl font-extrabold text-white tracking-tight leading-tight mb-5">
            Join the Team
          </h1>
          <p className="text-white/90 text-xl md:text-2xl font-light max-w-xl mx-auto leading-relaxed">
            Be part of a passionate crew crafting remarkable shading solutions. Flexible workdays, hands-on work, and a team that has your back.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-xl p-6 space-y-5">
              <h2 className="text-lg font-bold text-gray-900">What to Expect</h2>
              {[
                { icon: 'ri-tools-line', title: 'Hands-On Work', desc: 'Erect and dismantle our innovative stretch tent structures on-site.' },
                { icon: 'ri-calendar-line', title: 'Flexible Workdays', desc: 'Schedules tailored to each project. No two days are the same.' },
                { icon: 'ri-team-line', title: 'Great Team Culture', desc: 'Work alongside skilled, supportive crew members who take pride in their craft.' },
                { icon: 'ri-map-pin-line', title: 'Varied Locations', desc: 'Work at events, venues, and private functions across the region.' },
              ].map(item => (
                <div key={item.title} className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-teal-500 text-base`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-teal-500 rounded-2xl p-6 text-white">
              <h3 className="font-bold text-base mb-2">Questions?</h3>
              <p className="text-sm text-white/90 leading-relaxed mb-4">
                Reach out to us directly and we'll get back to you as soon as possible.
              </p>
              <a
                href="mailto:info@popprotents.com"
                className="text-sm font-semibold underline underline-offset-2 whitespace-nowrap"
              >
                info@popprotents.com
              </a>
            </div>
          </div>

          {/* Application Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Form</h2>
              <p className="text-sm text-gray-500 mb-8">Fields marked with <span className="text-teal-500">*</span> are required.</p>

              {submitStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-teal-500"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Application Submitted!</h3>
                  <p className="text-gray-600 text-sm mb-6">Thanks for applying. We'll review your application and be in touch soon.</p>
                  <Link
                    to="/"
                    className="inline-block bg-teal-500 hover:bg-teal-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors whitespace-nowrap"
                  >
                    Back to Home
                  </Link>
                </div>
              ) : (
                <form id="job-application-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name + Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Full Name <span className="text-teal-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        placeholder="Jane Smith"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Email <span className="text-teal-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="jane@email.com"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>

                  {/* Phone + Location */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Phone Number <span className="text-teal-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+1 (555) 000-0000"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Your Location
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="City, State"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>

                  {/* Experience */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Relevant Experience
                    </label>
                    <select
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm"
                    >
                      <option value="">Select experience level</option>
                      <option value="No experience">No experience, eager to learn</option>
                      <option value="Some experience">Some experience (events, construction, rigging)</option>
                      <option value="Experienced">Experienced in tent or shade structures</option>
                      <option value="Very experienced">Very experienced, multiple years in the industry</option>
                    </select>
                  </div>

                  {/* Availability */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Availability
                    </label>
                    <select
                      name="availability"
                      value={formData.availability}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm"
                    >
                      <option value="">Select availability</option>
                      <option value="Weekdays">Weekdays</option>
                      <option value="Weekends">Weekends</option>
                      <option value="Weekdays and weekends">Weekdays &amp; Weekends</option>
                      <option value="Flexible">Fully Flexible</option>
                    </select>
                  </div>

                  {/* Motivation */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Why do you want to join? <span className="text-teal-500">*</span>
                    </label>
                    <textarea
                      name="motivation"
                      value={formData.motivation}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="Tell us a bit about yourself and why you'd be a great fit..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm resize-none"
                    />
                    <div className="text-right text-xs text-gray-400 mt-1">
                      {formData.motivation.length}/500
                    </div>
                  </div>

                  {/* Heard from */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      How did you hear about us?
                    </label>
                    <input
                      type="text"
                      name="heardFrom"
                      value={formData.heardFrom}
                      onChange={handleChange}
                      placeholder="Google, social media, referral, etc."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm"
                    />
                  </div>

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      Something went wrong. Please try again.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-teal-500 hover:bg-teal-600 text-white py-4 rounded-lg font-bold text-sm uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
